import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import CartLoader from "../../../Helper/Loaders/CartLoader";
import SegmentedToggle from "../../../Components/SegmentedToggle";
import BucketSankeyChart from "../../../Charts/Sankey/BucketSankeyChart";
import SOHBreakdownDrillDownModal from "./SOHBreakdown/SOHBreakdownDrillDownModal";
import { getSOHHistoryChartData, getSOHHistoryDrillDownData } from "../../../Api/BatteryHealthApi";
import { getDefaultMonthWindow } from "../../../Helper/DatePicker/MonthRange";
import Events from "../../../Analytics/Events";
import { useBaseEntityInfo, usePagesContent } from "../../../Hooks/usePagesContent";
import { isFieldIndicatorsEnabled } from "../../../Components/FieldStatusIndicator";
import CODE from "../../../Static/Constants/StatusCodes";
import { useSohMigrationBuckets } from "./utils/sohMigrationBuckets";
import {
  resolveSohMigrationMonths,
  buildSohMigrationChartInput,
  buildMigrationDrillDownPoint,
  buildMigrationTransitionDrillDownPoint,
  withTransitionFilter,
  buildHistoryDrillDownOptions,
  shouldWaitForSohMigrationFilters,
  getSohHistorySchemaFilter,
} from "./utils/sohBucketMigration";
import {
  resolveSohMigrationLevels,
  shouldShowLevelToggle,
  getDefaultLevel,
  getLevelToggleOptions,
  getSohLevelNouns,
  getLevelTableTexts,
  getSohMigrationTableMetaKeys,
} from "./utils/sohMigrationLevels";

// The history drill-down reads the window, level and bucket list from the data point
// (buildHistoryDrillDownOptions); a ribbon's transition filter is appended here.
const fetchHistoryDrillDown = (
  globalFilters, sohBucket, select, metaInfo, page, pageSize, sortBy, additionalFilters, dataPoint
) => getSOHHistoryDrillDownData(
  globalFilters, sohBucket, select, metaInfo, page, pageSize, sortBy,
  withTransitionFilter(additionalFilters, dataPoint),
  buildHistoryDrillDownOptions(dataPoint)
);

const STATE_BOX_SX = { display: "flex", justifyContent: "center", alignItems: "center", minHeight: 320 };

/**
 * SOH bucket migration: a monthly sankey of units (batteries, packs or strings, per
 * the selected level) moving between SOH buckets
 * (Battery Intelligence > SOH & RUL). Clicking a bucket in a month opens the SOH
 * drill-down for that bucket and month; clicking a ribbon opens it for the units
 * that made that move between the two months.
 */
export const DEFAULT_SOH_MIGRATION_TITLE = "SOH Bucket Migration";

// `months`: org meta charts.sohMigrationSankeyChart.months (resolved: default 12, max 12).
// `levels`: org meta charts.sohMigrationSankeyChart.levels (resolved: default ["battery"]).
// `buckets`: org meta charts.sohMigrationSankeyChart.buckets (see utils/sohMigrationBuckets;
// without a usable list the org-wide stateOfHealthBuckets are used).
// `waitForOperationalStatus`: the page shows the operational-status filter, so the fetch
// waits for it to settle (shouldWaitForSohMigrationFilters).
const SOHBucketMigrationChart = ({
  globalFilters = {}, title = DEFAULT_SOH_MIGRATION_TITLE, months, levels, buckets, waitForOperationalStatus = false,
}) => {
  const { sohBuckets, requestBuckets } = useSohMigrationBuckets(buckets);

  // Unit level counted: the first configured level, switchable when there are several.
  const levelOptions = useMemo(() => resolveSohMigrationLevels(levels), [levels]);
  const [level, setLevel] = useState(() => getDefaultLevel(levelOptions));
  useEffect(() => {
    if (!levelOptions.includes(level)) setLevel(getDefaultLevel(levelOptions));
  }, [levelOptions, level]);

  // Nouns follow the level: the org's base entity labels at battery level, else pack / string.
  const { baseEntityTypeLable, baseEntityTypePluralLable } = useBaseEntityInfo();
  const { singular: nounSingular, plural: nounPlural } = getSohLevelNouns(level, {
    baseSingular: baseEntityTypeLable,
    basePlural: baseEntityTypePluralLable,
  });
  const tableTexts = getLevelTableTexts({ plural: nounPlural });
  const tableMetaKeys = useMemo(() => getSohMigrationTableMetaKeys(level), [level]);

  // Operational-status marker on the drill-down's vehicle cell, only when the org has
  // globalConfig["fieldIndicators.enabled"] on; then the request also asks for
  // operationalStatus so the marker has a value to read.
  const pagesContent = usePagesContent();
  const showOperationalStatus = isFieldIndicatorsEnabled(pagesContent?.metaInfo);
  const schemaFilter = getSohHistorySchemaFilter(level, showOperationalStatus);

  // Fixed window: the last N complete months (org meta, default 12), ending with the
  // previous month (the current month's SOH is usually not imported yet).
  const monthCount = resolveSohMigrationMonths(months);
  const range = useMemo(() => getDefaultMonthWindow(monthCount), [monthCount]);

  const [apiData, setApiData] = useState([]);
  // Starts loading: the first paint shows the loader, not the "No SOH data" empty state.
  const [isLoading, setIsLoading] = useState(true);
  const filtersPending = shouldWaitForSohMigrationFilters(waitForOperationalStatus, globalFilters);
  const [hasError, setHasError] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDataPoint, setSelectedDataPoint] = useState(null);

  useEffect(() => {
    if (!range?.startDate || !range?.endDate) return undefined;
    // Stay on the loader until the filters settle; the next run fetches once.
    if (filtersPending) {
      setIsLoading(true);
      return undefined;
    }
    let cancelled = false;

    const fetchData = async () => {
      setIsLoading(true);
      setHasError(false);
      try {
        const res = await getSOHHistoryChartData(globalFilters, { ...range, entityType: level, sohBuckets: requestBuckets });
        if (cancelled) return;
        if (res?.responseStatus?.code === CODE.SUCCESS) {
          const data = res?.response?.data;
          setApiData(Array.isArray(data) ? data : []);
        } else {
          setApiData([]);
          setHasError(true);
        }
      } catch (err) {
        console.error("SOHBucketMigration: fetch error", err);
        if (!cancelled) {
          setApiData([]);
          setHasError(true);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [globalFilters, range, level, requestBuckets, filtersPending]);

  const chartInput = useMemo(() => buildSohMigrationChartInput(apiData, sohBuckets), [apiData, sohBuckets]);

  const handleNodeClick = useCallback((click) => {
    const point = buildMigrationDrillDownPoint(click, sohBuckets);
    if (!point) return;
    Events("Battery Intelligence: opened SOH Bucket Migration drill down", {
      month: point.monthKey,
      sohBucket: point.sohCategory,
      entityType: level,
    });
    setSelectedDataPoint({ ...point, entityType: level, requestBuckets });
    setModalOpen(true);
  }, [sohBuckets, level, requestBuckets]);

  const handleFlowClick = useCallback((click) => {
    const point = buildMigrationTransitionDrillDownPoint(click, sohBuckets);
    if (!point) return;
    Events("Battery Intelligence: opened SOH Bucket Migration transition drill down", {
      fromMonth: point.fromMonthKey,
      toMonth: point.toMonthKey,
      fromSohBucket: point.fromBucket,
      toSohBucket: point.toBucket,
      entityType: level,
    });
    setSelectedDataPoint({ ...point, entityType: level, requestBuckets });
    setModalOpen(true);
  }, [sohBuckets, level, requestBuckets]);

  // Switching level refetches the chart (effect above) and closes any open drill-down.
  const handleLevelChange = useCallback((next) => {
    if (!next || next === level) return;
    Events("Battery Intelligence: changed SOH Bucket Migration level", { entityType: next });
    setModalOpen(false);
    setSelectedDataPoint(null);
    // Loader in the same render as the new level, so the old chart never shows with
    // the new level's nouns.
    setIsLoading(true);
    setLevel(next);
  }, [level]);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSelectedDataPoint(null);
  }, []);

  let body;
  if (isLoading) {
    body = (
      <Box sx={STATE_BOX_SX} data-testid="soh-bucket-migration-loading">
        <CartLoader />
      </Box>
    );
  } else if (hasError) {
    body = (
      <Box sx={STATE_BOX_SX} data-testid="soh-bucket-migration-error">
        <Typography variant="fw400s14c686868">Unable to load SOH bucket migration. Please try again later.</Typography>
      </Box>
    );
  } else if (!chartInput.hasData) {
    body = (
      <Box sx={STATE_BOX_SX} data-testid="soh-bucket-migration-empty">
        <Typography variant="fw400s14c686868">No SOH data for the selected months</Typography>
      </Box>
    );
  } else {
    body = (
      <BucketSankeyChart
        columns={chartInput.columns}
        buckets={chartInput.buckets}
        nodes={chartInput.nodes}
        flows={chartInput.flows}
        nounSingular={nounSingular}
        nounPlural={nounPlural}
        columnNoun="month"
        onNodeClick={handleNodeClick}
        onFlowClick={handleFlowClick}
        dataTestId="soh-bucket-migration-chart"
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "8px",
          border: "1px solid #E6E6E6",
          maxWidth: "calc(100vw - 102px)",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        {/* ── Header (same as the State of Health Breakdown card) ──────────── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: "28px",
            py: "24px",
          }}
        >
          <Typography variant="fw600s16lh24c444B51" sx={{ color: "#2E2E2E" }}>
            {title || DEFAULT_SOH_MIGRATION_TITLE}
          </Typography>
          {shouldShowLevelToggle(levelOptions) && (
            <Box data-testid="soh-migration-level-toggle">
              <SegmentedToggle
                label="Level"
                options={getLevelToggleOptions(levelOptions, { baseLabel: baseEntityTypeLable })}
                value={level}
                onChange={handleLevelChange}
                testIdPrefix="soh-migration-level"
              />
            </Box>
          )}
        </Box>

        {/* ── Chart: legend, plot, month axis, caption ─────────────────────── */}
        <Box sx={{ px: "28px", pb: "20px", minWidth: 0 }}>{body}</Box>
      </Box>

      {/* ── Drill-down: same modal as the SOH Breakdown, history endpoint ──── */}
      <SOHBreakdownDrillDownModal
        open={modalOpen}
        onClose={handleModalClose}
        selectedDataPoint={selectedDataPoint}
        globalFilters={globalFilters}
        fetchDrillDownData={fetchHistoryDrillDown}
        schemaFilter={schemaFilter}
        tableTitle={tableTexts.tableTitle}
        emptyMessage={tableTexts.emptyMessage}
        subtitle={
          selectedDataPoint?.subtitle
          ?? (selectedDataPoint?.monthLabel ? `Month: ${selectedDataPoint.monthLabel}` : null)
        }
        hideListActions={true}
        operationalStatusIndicator={showOperationalStatus}
        tableMetaKeys={tableMetaKeys}
      />
    </>
  );
};

export default SOHBucketMigrationChart;
